import React from 'react';
import {Modal, Button, DataTable, DataTableColumn, Icon} from '@salesforce/design-system-react';
import {CONSTANT_ACCESS_REQUEST} from "../constants/accessRequest";
import {CONSTANT_ACCOUNT} from "../constants/accountManagement";

type AccessRequestData = {
    crt_id: string;
    status: string;
    account_id: string;
    error_status: string;
}

type AccessRequestConfirmationModalProps = {
    isOpen: boolean;
    toggleModal: (isOpen: boolean) => void;
    data: AccessRequestData[];
    crt_id: string;
};

const BulkAccessRequestStatusModal: React.FC<AccessRequestConfirmationModalProps> = ({
                                                                                         isOpen,
                                                                                         toggleModal,
                                                                                         data,
                                                                                         crt_id,
                                                                                     }) => {

    const completedCount = data.filter(acc => acc.status.toLowerCase() === "success" && !!acc.account_id).length;
    const failedCount = data.filter(acc => acc.status.toLowerCase() === "failure").length;
    const inProgressCount = data.filter(acc => acc.status.toLowerCase() === "processing").length;

    const overallStatus = inProgressCount > 0 ? "Processing" : "Completed";

    // Select icon and color based on status
    const statusIcon = overallStatus === "Completed" ? "success" : "warning";
    const statusColor = overallStatus === "Completed" ? "slds-icon-text-success" : "slds-icon-text-warning";

    // Filter data for display
    const filteredData = overallStatus === "Completed"
        ? data.filter(acc => acc.status.toLowerCase() === "success" && !!acc.account_id)
        : [];

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={() => toggleModal(false)}
            heading={CONSTANT_ACCOUNT.ACCOUNT_VENDING_STATUS}
            size={"large"}
            footer={[
                <Button label={CONSTANT_ACCESS_REQUEST.CANCEL} onClick={() => toggleModal(false)}/>
            ]}
        >
            <div className="slds-p-around_medium">
                <div className="slds-m-bottom_medium">
                    <strong>CRT ID: {crt_id}</strong>
                </div>

                {/* Status with colored icon */}
                <div className="slds-m-bottom_medium">
                    <strong>Overall Status: {overallStatus}</strong>
                    <span className="slds-m-left_x-small">
                        <Icon
                            category="utility"
                            name={statusIcon}
                            size="small"
                            className={statusColor}
                        />
                    </span>
                </div>

                <div className="slds-grid slds-gutters slds-m-bottom_medium">
                    <div className="slds-col">
                        <span className="slds-badge slds-theme_success">
                            Success: {completedCount}
                        </span>
                        <span className="slds-badge slds-theme_error">
                            Failed: {failedCount}
                        </span>
                        <span className="slds-badge slds-theme_warning">
                            In Progress: {inProgressCount}
                        </span>
                    </div>
                </div>

                {/* Show the failed note only when all processing is completed */}
                {overallStatus === "Completed" && failedCount > 0 && (
                    <div className="slds-m-bottom_medium slds-text-color_error">
                        <strong>Note:</strong> {failedCount} account(s) failed. Please re-create them through the account vending process again.
                    </div>
                )}

                {filteredData.length > 0 ? (
                    <DataTable items={filteredData} className="slds-table slds-table_bordered slds-table_cell-buffer">
                        <DataTableColumn label={CONSTANT_ACCOUNT.ACCOUNT_ID} property="account_id"/>
                        <DataTableColumn label={CONSTANT_ACCOUNT.STATUS} property="status"/>
                    </DataTable>
                ) : (
                    <p>The account vending process is still in progress. Please wait until
                        it's fully completed.</p>
                )}
            </div>
        </Modal>
    );
};

export default BulkAccessRequestStatusModal;